import React, { useCallback, useEffect, useState } from 'react';
import { Grid, LinearProgress } from '@material-ui/core';
import { withTheme } from '@material-ui/styles';
import { useHistory, useLocation, useParams } from 'react-router';
import SingleActivityContainer from '../../containers/SingleActivityContainer';
import ActivityCard from '../../components/ActivityCard';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';
import CompareActivities from '../../components/CompareActivities';
import ActivitiesTable from './components/ActivitiesTable';
import ActivityFilter from './components/ActivityFilter';
import { useNotify } from '../../hooks/useNotify';
import { getUrlFromFilter, getFilterFromUrl } from './components/urlFiltering';
import moment from 'moment';
import { useGA } from '../../hooks/useGoogleAnalytics';
import { useIslands } from '../../hooks/useIslands';
import TicketBlock from './components/TicketBlock';
import ActivityActionButtons from './components/ActivityActionButtons';

function PageBottomTrigger({ hasMore, loadMore, loading }) {
    const divRef = React.useRef(null);

    // observe the divRef
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore) {
                    console.log('Loading more activities');
                    loadMore();
                }
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 1.0
            }
        );
        if (divRef.current) {
            observer.observe(divRef.current);
        }
        return () => {
            if (divRef.current) {
                observer.unobserve(divRef.current);
            }
        };
    }, [divRef, hasMore, loadMore]);

    // 100vh is pushing the div down
    return (
        <>
            {loading && (
                <Grid
                    item
                    style={{ height: '100vh', paddingTop: '20%' }}
                    container
                    className="mb-3"
                    justifyContent="center"
                    alignItems="center"
                >
                    <div className="search-loading">
                        <div className="mb-2">Loading activities</div>
                        <LinearProgress className="search-bar" />
                    </div>
                </Grid>
            )}
            <div ref={divRef}></div>
        </>
    );
}

function useInfiniteLoadingActivities(getAllActivityFilters) {
    const PAGE_SIZE = 6;

    const [activities, setActivities] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // Whenever filters change, reset everything
    useEffect(() => {
        setActivities([]);
        setCurrentPage(0);
        setHasMore(true);
    }, [JSON.stringify(getAllActivityFilters)]);

    // Fetch whenever currentPage or filters change
    useEffect(() => {
        // Don’t fetch if we know there’s no more
        if (!hasMore) {
            return;
        }

        setLoading(true);
        doSwaggerCall('activities', 'getAllActivities', {
            ...getAllActivityFilters,
            skip: currentPage * PAGE_SIZE,
            take: PAGE_SIZE
        })
            .then(data => {
                setActivities(prev => {
                    // directly override the prev section with the skip + take parts
                    console.log({ currentPage, d: data?.items?.map(e => e.name) });
                    return [
                        ...prev.slice(0, currentPage * PAGE_SIZE),
                        ...data.items,
                        ...prev.slice(currentPage * PAGE_SIZE + PAGE_SIZE)
                    ];
                });
                // If we got fewer than a full page, there’s no more to load
                setHasMore(data.items.length === PAGE_SIZE);
            })
            .catch(err => {
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [currentPage, JSON.stringify(getAllActivityFilters), hasMore]);

    // loadMore will only bump the page if we’re not already loading
    const loadMore = useCallback(() => {
        if (!loading && hasMore) {
            setCurrentPage(p => p + 1);
        }
    }, [loading, hasMore]);

    return { activities, loading, hasMore, loadMore };
}

function ActivityCardPage({
    theme,
    isAgent,
    isAdmin,
    customerData,
    compareActivities,
    addActivityToCompareList,
    clearCompareList,
    removeActivityFromCompareList
}) {
    const history = useHistory();
    const location = useLocation();
    const urlParams = useParams();

    const queryParams = new URLSearchParams(window.location.search);
    const isActivityDetailedView = queryParams.get('activity') !== null;

    const [currentFilters, setFilters] = useState({
        ...getFilterFromUrl(location.search)
    });

    useEffect(() => {
        if (!customerData || !customerData.arrival_date || !customerData.departure_date) {
            return setFilters({
                ...getFilterFromUrl(location.search),
                checkInDate: moment().startOf('day'),
                checkOutDate: moment()
                    .add(6, 'day')
                    .startOf('day')
            });
        }

        const checkInDate = moment(customerData.arrival_date, 'YYYY-MM-DD');
        const checkOutDate = moment(customerData.departure_date, 'YYYY-MM-DD');
        setFilters(prev => ({
            ...prev,
            checkInDate,
            checkOutDate
        }));
    }, [customerData, location.search]);

    const { notifyError } = useNotify();
    const [showCommission, setShowCommission] = useState(false);
    const [showTicketSellingRanges, setShowTicketSellingRanges] = useState(true);
    const [hideLongExpiredTickets, setHideLongExpiredTickets] = useState(true);

    const [islandOptions] = useIslands();

    const { track } = useGA();

    let vendorId;
    if (typeof urlParams.vendorId !== 'undefined') {
        vendorId = urlParams.vendorId;
    }

    // Convert island IDs to comma-separated island names for API call
    const getIslandNamesFromIds = useCallback(
        islandIds => {
            if (!islandIds || !Array.isArray(islandIds) || islandIds.length === 0) {
                return undefined;
            }

            // If '0' (All) is selected or if admin and includes '0', return undefined
            if (islandIds.includes('0')) {
                return undefined;
            }

            // Convert IDs to names
            const names = islandIds
                .map(id => {
                    const island = islandOptions.find(i => String(i.id) === String(id));
                    return island ? island.name : null;
                })
                .filter(name => name !== null);

            // Return as comma-separated string for API
            return names.length > 0 ? names.join(',') : undefined;
        },
        [islandOptions]
    );

    // on url change, update filters
    useEffect(() => {
        setFilters(getFilterFromUrl(location.search));
    }, [location.search, setFilters]);

    // set url based on new filters
    const setCurrentFilters = useCallback(
        newfilters => {
            history.replace(getUrlFromFilter(newfilters, isAgent, isAdmin));
        },
        [history, isAgent, isAdmin]
    );

    // When restricted to one island, use default island names directly from client config
    // to avoid race conditions with stale localStorage/URL values on first render
    const getIslandParam = () => {
        if (window.clientData?.restrictedToOneIsland) {
            const defaults = window.clientData.defaultIsland;
            return Array.isArray(defaults) && defaults.length > 0
                ? defaults.join(',')
                : undefined;
        }
        return getIslandNamesFromIds(currentFilters.islands);
    };

    const { activities, loading, hasMore, loadMore } = useInfiniteLoadingActivities({
        island: getIslandParam(),
        sorter: currentFilters.sorter,
        tags: typeof vendorId === 'undefined' ? currentFilters.tags : undefined,
        search: typeof vendorId === 'undefined' ? currentFilters.search : undefined,
        role: isAgent ? 'agent' : '',
        fromDate: currentFilters.checkInDate.format('YYYY-MM-DD'),
        toDate: currentFilters.checkOutDate.format('YYYY-MM-DD'),
        groupSize: currentFilters.guests,
        exclude_api: 'GolfApi',
        // only defined if we are checking one specific vendor
        vendorId
    });

    let activityHasTimeslot = activities || [];

    if (currentFilters.showEmpty === false) {
        activityHasTimeslot = activities ? activities?.filter(e => e.firstTimeslot) : [];
    }

    // Track view_item_list event when activities are loaded
    useEffect(() => {
        if (activityHasTimeslot && activityHasTimeslot.length > 0 && !loading) {
            const items = [];
            activityHasTimeslot.forEach((activity, idx) => {
                activity.tickets.forEach(ticket => {
                    items.push({
                        index: idx + 1,
                        item_id: `${activity.id.toString()}-${ticket.id.toString()}`,
                        item_name: `${activity.name} | ${ticket.name}`,
                        price: ticket.retail_price || 0,
                        item_category: activity.name
                    });
                });
            });

            const value = items.reduce((sum, item) => sum + item.price, 0);

            track.viewItemList({
                items,
                value,
                item_list_name: 'Activities List',
                item_list_id: currentFilters?.islands || 'all'
            });
        }
    }, [activityHasTimeslot.length, loading, currentFilters?.islands, track, activityHasTimeslot]);

    const loadingAndDoesNotHaveData = activityHasTimeslot?.length === 0 && loading;

    return (
        <Grid className="golf-reservation" container>
            <ActivityFilter
                isAgent={isAgent}
                isAdmin={isAdmin}
                filters={currentFilters}
                setFilters={setCurrentFilters}
                vendorActivitySelected={typeof vendorId !== 'undefined'}
                guestTimeRange={
                    customerData && customerData.arrival_date && customerData.departure_date
                        ? {
                              checkInDate: moment(customerData.arrival_date, 'YYYY-MM-DD'),
                              checkOutDate: moment(customerData.departure_date, 'YYYY-MM-DD')
                          }
                        : null
                }
            />
            <Grid
                item
                container
                lg={9}
                sm={8}
                xs={12}
                justifyContent="flex-start"
                style={{ position: 'relative' }}
            >
                {(isAgent || isAdmin) && !isActivityDetailedView && (
                    <div style={{ position: 'absolute', right: 10, top: 1 }}>
                        <ActivityActionButtons
                            showCommission={showCommission}
                            setShowCommission={setShowCommission}
                            showTicketSellingRanges={showTicketSellingRanges}
                            setShowTicketSellingRanges={setShowTicketSellingRanges}
                            hideLongExpiredTickets={hideLongExpiredTickets}
                            setHideLongExpiredTickets={setHideLongExpiredTickets}
                        />
                    </div>
                )}
                <Grid
                    container
                    alignContent="space-between"
                    style={{
                        paddingLeft: '48px',
                        paddingTop: '16px',
                        paddingRight: '16px',
                        paddingBottom: '16px',
                        minHeight: '100vh'
                    }}
                >
                    {currentFilters.activityId ? (
                        <SingleActivityContainer
                            activityId={currentFilters.activityId}
                            checkInDate={currentFilters.checkInDate}
                            checkOutDate={currentFilters.checkOutDate}
                            groupSize={currentFilters.guests}
                            onNavigateBack={() =>
                                setCurrentFilters({
                                    ...currentFilters,
                                    activityId: false
                                })
                            }
                        />
                    ) : (
                        <>
                            <Grid
                                container
                                spacing={4}
                                alignItems="stretch"
                                style={{ paddingTop: '2rem', marginBottom: '16px' }}
                            >
                                {!loading && !hasMore && activityHasTimeslot?.length === 0 && (
                                    <Grid
                                        item
                                        container
                                        className="mb-3"
                                        style={{ paddingTop: '20%' }}
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        <h1 style={{ textAlign: 'center' }}>
                                            No activities match this filter, please try another one.
                                        </h1>
                                    </Grid>
                                )}
                                {!loadingAndDoesNotHaveData &&
                                    currentFilters.showAsTable &&
                                    (isAgent || isAdmin) && (
                                        <ActivitiesTable
                                            showCommission={showCommission}
                                            setShowCommission={setShowCommission}
                                            isAdmin={isAdmin}
                                            activities={activities}
                                            currentFilters={currentFilters}
                                            setCurrentFilters={setCurrentFilters}
                                            showSellingRanges={showTicketSellingRanges}
                                            setShowSellingRanges={setShowTicketSellingRanges}
                                        />
                                    )}
                                {!loadingAndDoesNotHaveData &&
                                    (!currentFilters.showAsTable || (!isAgent && !isAdmin)) &&
                                    activityHasTimeslot.map(activity => (
                                        <ActivityCard
                                            key={activity.id}
                                            activity={activity}
                                            currentFilters={currentFilters}
                                            onClick={() =>
                                                setCurrentFilters({
                                                    ...currentFilters,
                                                    activityId: activity.id
                                                })
                                            }
                                            // if the activity is currently in the comparison page
                                            isCompared={compareActivities
                                                .map(e => e.id)
                                                .includes(activity.id)}
                                            // toggle comparison
                                            toggleCompare={minTicketRetailPrice => {
                                                // TODO: refactor this one to reuse isCompared on this level
                                                const isCompared = compareActivities
                                                    .map(e => e.id)
                                                    .includes(activity.id);

                                                // remove from compare list
                                                if (isCompared) {
                                                    removeActivityFromCompareList(activity.id);
                                                    return;
                                                }

                                                // add to compare list
                                                if (compareActivities.length > 2) {
                                                    notifyError(
                                                        'You can compare maximum 3 activities at the same time!'
                                                    );
                                                    return;
                                                }

                                                addActivityToCompareList({
                                                    ...activity,
                                                    minTicketRetailPrice
                                                });
                                            }}
                                        >
                                            <TicketBlock
                                                activity={activity}
                                                isAdminOrAgent={isAgent || isAdmin}
                                                showCommission={showCommission}
                                                currentFilters={currentFilters}
                                                showTicketSellingRanges={showTicketSellingRanges}
                                            />
                                        </ActivityCard>
                                    ))}
                                <PageBottomTrigger
                                    hasMore={hasMore}
                                    loadMore={loadMore}
                                    offsetY={200}
                                    loading={loading}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
                <CompareActivities
                    compareActivities={compareActivities}
                    removeActivityFromCompareList={removeActivityFromCompareList}
                    clearCompareList={clearCompareList}
                />
            </Grid>
        </Grid>
    );
}

export default withTheme(ActivityCardPage);
